import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDownIcon, ShieldAlertIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { customer, allergySummary, severityLabel, severityTone } from '../../data/customer';
import { spring } from '../../utils/motion';
import { cn } from '../../utils/cn';

/** Pinned above the composer in every thread — the record follows the booking, not the conversation. */
export function AllergyPin({
  acknowledged = false
}: {
  acknowledged?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion();
  const worst = customer.dietary.allergies.some(a => a.severity === 'anaphylaxis');
  return <div className={cn('rounded-lg border', worst ? 'border-danger/40 bg-danger-soft' : 'border-hairline bg-oat')} data-id="element-112" data-mp="7pac4nqr0vdi">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} className="flex w-full items-center gap-2 px-3 py-2 text-left" data-id="element-113" data-mp="2xd18vid56ag3">
        <ShieldAlertIcon className={cn('h-4 w-4 shrink-0', worst ? 'text-danger' : 'text-action')} aria-hidden="true" data-id="element-114" data-mp="32raoz2khz9bm" />
        <span className={cn('min-w-0 flex-1 truncate text-[13px]', worst ? 'text-danger' : 'text-ink-2')} data-id="element-115" data-mp="37n9krb85ntm2">
          <span className="font-semibold" data-id="element-116" data-mp="2xrfy2z0q2jqk">Dietary record: </span>
          {allergySummary()}
        </span>
        {acknowledged && <Badge tone="success" data-id="element-117" data-mp="2jzhvl71inm3d">Acknowledged</Badge>}
        <ChevronDownIcon className={cn('h-4 w-4 shrink-0 text-ink-3 transition-transform duration-200 ease-out', open && 'rotate-180')} aria-hidden="true" data-id="element-118" data-mp="2jyq86ji2ouo6" />
      </button>

      <AnimatePresence initial={false} data-id="element-119" data-mp="29zr0b354t328">
        {open && <motion.div initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        height: 0
      }} animate={reduce ? {
        opacity: 1
      } : {
        opacity: 1,
        height: 'auto'
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        height: 0
      }} transition={reduce ? {
        duration: 0.14
      } : spring.snappy} className="overflow-hidden" data-id="element-120" data-mp="2f1zx6al4kp9m">
            <div className="space-y-2.5 border-t border-hairline px-3 py-3" data-id="element-121" data-mp="6tlbyf23b7j1">
              <ul className="space-y-2" data-id="element-122" data-mp="6riamyvhlrz0">
                {customer.dietary.allergies.map(allergy => {
              const member = customer.dietary.members.find(m => m.id === allergy.memberId);
              return <li key={allergy.id} className="flex items-start justify-between gap-3" data-id="element-123" data-mp="ujfm2ov0yhzl">
                      <span className="min-w-0" data-id="element-124" data-mp="pjb4swmalj8b">
                        <span className="block text-sm font-medium text-ink" data-id="element-125" data-mp="pkm70s03aixb">
                          {allergy.allergen}
                          {member ? <span className="font-normal text-ink-3" data-id="element-126" data-mp="gj4ufjlw4zdd"> · {member.name}</span> : null}
                        </span>
                        {allergy.note && <span className="block text-[12px] text-ink-2" data-id="element-127" data-mp="35bql94hmpj11">{allergy.note}</span>}
                      </span>
                      <Badge tone={severityTone[allergy.severity]} data-id="element-128" data-mp="3182cwjl6ajeq">
                        {severityLabel[allergy.severity]}
                      </Badge>
                    </li>;
            })}
              </ul>
              <p className="text-[12px] text-ink-2" data-id="element-129" data-mp="1ps9gd6j8vo0k">
                <span className="font-medium text-ink" data-id="element-130" data-mp="1pqgahtggh7tm">Also: </span>
                {customer.dietary.diets.join(', ')} · dislikes {customer.dietary.dislikes.join(', ').toLowerCase()} · chilli
                tolerance {customer.dietary.spiceTolerance}/5.
              </p>
              <p className="text-[12px] text-ink-3" data-id="element-131" data-mp="24fuioy8apj7f">{customer.dietary.notes}</p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}